import { Box, Typography } from "@mui/material";
import { MONO, eyebrow, tokens } from "./../theme";

const fmt = (n) => Math.round(n || 0).toLocaleString();

// The signature element. A segmented bar that visualizes the VRAM-packing
// scheduler: physical `used` (solid signal gradient) + the scheduler's
// `reserved` headroom beyond that (striped) + `free`. Data reads in mono.
function LegendCell({ swatch, striped, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "3px",
          flex: "0 0 auto",
          background: striped
            ? "repeating-linear-gradient(45deg, rgba(46,139,230,.55) 0 3px, rgba(46,139,230,.15) 3px 6px)"
            : swatch,
          border: swatch === "#eaf0f8" ? `1px solid ${tokens.line}` : "none",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: MONO, fontSize: ".8rem", fontWeight: 600, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ ...eyebrow, fontSize: ".58rem", letterSpacing: ".1em" }}>{label}</Typography>
      </Box>
    </Box>
  );
}

export default function VramGauge({ usedMb = 0, reservedMb = 0, totalMb = 0, label = "VRAM" }) {
  const pct = (v) => (totalMb ? Math.min(100, Math.max(0, (v / totalMb) * 100)) : 0);
  const usedPct = pct(usedMb);
  const overhang = Math.max(0, pct(reservedMb) - usedPct); // reserved beyond physical use
  const freeMb = Math.max(0, totalMb - Math.max(usedMb, reservedMb));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.9 }}>
        <Typography sx={eyebrow}>{label}</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: ".82rem", fontWeight: 600 }}>
          {fmt(usedMb)}
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
            {" "}
            / {fmt(totalMb)} MB
          </Box>
        </Typography>
      </Box>

      <Box sx={{ position: "relative", height: 14, borderRadius: 7, background: "#eaf0f8", overflow: "hidden" }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${usedPct}%`,
            background: tokens.vramGradient,
            transition: "width .5s cubic-bezier(.4,0,.2,1)",
          }}
        />
        {overhang > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${usedPct}%`,
              width: `${overhang}%`,
              background: "repeating-linear-gradient(45deg, rgba(46,139,230,.4) 0 5px, rgba(46,139,230,.1) 5px 10px)",
              transition: "all .5s cubic-bezier(.4,0,.2,1)",
            }}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2.25, mt: 1.1 }}>
        <LegendCell swatch={tokens.vramGradient} label="used" value={fmt(usedMb)} />
        {reservedMb > 0 && <LegendCell striped label="reserved" value={fmt(reservedMb)} />}
        <LegendCell swatch="#eaf0f8" label="free" value={fmt(freeMb)} />
      </Box>
    </Box>
  );
}
